import axios from 'axios';
import router from '@/router';



const api = axios.create({ baseURL:'/api' });

const MAINTENANCE_ROUTE = '/api/etat-site';
const MAINTENANCE_CHECK_INTERVAL = 30 * 1000; // 30 secondes en millisecondes

let lastCheck = 0;
let lastStatus = null;
let lastCheckPromise = null;

let isRefreshing = false;
let queue = [];

function processQueue(err, token = null) {
    queue.forEach(p => err ? p.reject(err) : p.resolve(token));
    queue = [];
}

async function checkMaintenance() {
    const now = Date.now();
    
    // Si une vérification est déjà en cours, attendre le résultat
    if (lastCheckPromise) {
        return lastCheckPromise;
    }
    
    // Si on a un statut récent et valide, l'utiliser
    if (lastStatus !== null && now - lastCheck < MAINTENANCE_CHECK_INTERVAL) {
        if (lastStatus === true) {
            const err = new Error("Site en maintenance");
            err.isMaintenance = true;
            throw err;
        }
        // Site OK, utiliser le cache (pas de log pour éviter le spam)
        return;
    }
    
    // Effectuer une nouvelle vérification
    console.log('🔍 Vérification de l\'état du site (cache expiré)');
    lastCheckPromise = axios.get(MAINTENANCE_ROUTE)
        .then(res => {
            lastCheck = now;
            lastStatus = res.data.maintenance;
            if (lastStatus === true) {
                const err = new Error("Site en maintenance");
                err.isMaintenance = true;
                throw err;
            }
            return lastStatus;
        })
        .catch(() => {
            lastCheck = now;
            lastStatus = true; // En cas d'erreur, considérer comme maintenance
            const err = new Error("Site inaccessible ou en maintenance");
            err.isMaintenance = true;
            throw err;
        })
        .finally(() => {
            lastCheckPromise = null;
        });
    
    return lastCheckPromise;
}


api.interceptors.request.use(async cfg => {
    if (!cfg.url.endsWith(MAINTENANCE_ROUTE)) {
        try {
            await checkMaintenance();
        } catch (e) {
            // Ne plus rediriger vers la page maintenance
            // Juste afficher un message d'erreur et continuer
            console.warn("API injoignable lors de la vérification de maintenance:", e.message);
            // On continue la requête malgré l'erreur de maintenance
        }
    }

    const token = localStorage.getItem('access_token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

// Le reste ne change pas
api.interceptors.response.use(
    res => res,
    async err => {
        // 1. Détection des erreurs réseau ou API down/proxy nginx
        const networkErrors = [
            'ECONNREFUSED',
            'ERR_NETWORK',
            'ERR_PROXY_CONNECTION_FAILED',
            'ERR_CONNECTION_REFUSED',
            'ERR_CONNECTION_RESET'
        ];
        if (!err.response || networkErrors.includes(err.code)) {
            // Ne plus rediriger vers la page maintenance
            // Juste afficher un message d'erreur et rejeter l'erreur
            console.warn("Erreur réseau - API injoignable:", err.message || err.code);
            const error = new Error("API injoignable");
            error.isNetworkError = true;
            return Promise.reject(error);
        }

        // 2. Détection explicite maintenance (depuis ton checkMaintenance si tu utilises ça)
        if (err.isMaintenance && router.currentRoute.value.name !== 'Maintenance') {
            router.push({ name: 'Maintenance' });
            return Promise.reject(err);
        }

        // 3. Gestion du refresh JWT si besoin
        const orig = err.config;
        if (err.response?.status === 403 && !orig._retry) {
            orig._retry = true;
            if (isRefreshing) {
                return new Promise((res, rej) => queue.push({ resolve:res, reject:rej }))
                    .then(token => {
                        orig.headers.Authorization = `Bearer ${token}`;
                        return api(orig);
                    });
            }
            isRefreshing = true;
            const refresh = localStorage.getItem('refresh_token');
            return new Promise((resolve, reject) => {
                axios.post('/api/auth/refresh/', { refresh })
                    .then(({ data }) => {
                        localStorage.setItem('access_token', data.access);
                        api.defaults.headers.Authorization = `Bearer ${data.access}`;
                        processQueue(null, data.access);
                        orig.headers.Authorization = `Bearer ${data.access}`;
                        resolve(api(orig));
                    })
                    .catch(e => {
                        processQueue(e, null);
                        localStorage.clear();
                        router.push({ name:'Home' });
                        reject(e);
                    })
                    .finally(() => { isRefreshing = false; });
            });
        }

        // 4. Toute autre erreur normale
        return Promise.reject(err);
    }
);

export default api;
